import { enhancedApi } from '@store/generated/encounter-types';
import { EncounterType, CreateEncounterTypeRequest } from '../types';

/**
 * Feature-specific tag types for encounter types cache management
 * Includes AppointmentsList for cross-feature cache invalidation
 */
const ENCOUNTER_TYPE_TAG_TYPES = ['EncounterType', 'EncounterTypesList', 'AppointmentsList'] as const;

/**
 * Encounter Types API - extends the generated OpenAPI client
 * This ensures we're using generated types and endpoints
 * while maintaining feature-specific customization
 */
export const encounterTypesApi = enhancedApi
  .enhanceEndpoints({
    addTagTypes: ENCOUNTER_TYPE_TAG_TYPES,
    endpoints: {
      // Enhance generated endpoints with cache tags and optimistic updates
      getV2ProviderEncounterTypes: {
        providesTags: result =>
          result
            ? [
                ...result.items.map(({ encounter_type_id }) => ({
                  type: 'EncounterType' as const,
                  id: encounter_type_id,
                })),
                { type: 'EncounterTypesList' as const, id: 'LIST' },
              ]
            : [{ type: 'EncounterTypesList', id: 'LIST' }],
      },

      postV2ProviderEncounterTypes: {
        invalidatesTags: [
          { type: 'EncounterTypesList', id: 'LIST' },
          { type: 'AppointmentsList', id: 'LIST' }, // Invalidate appointments for new type
        ],
        // Add optimistic updates if needed
        onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
          try {
            await queryFulfilled;
            // Refetch the list after successful creation
          } catch (error) {
            // Handle error - basic error display for now
            console.error('Failed to create encounter type:', error);
          }
        },
      },

      patchV2ProviderEncounterTypesByEncounterTypeId: {
        invalidatesTags: (result, error, { encounterTypeId }) => [
          { type: 'EncounterType', id: encounterTypeId },
          { type: 'EncounterTypesList', id: 'LIST' },
          { type: 'AppointmentsList', id: 'LIST' }, // Invalidate appointments to refresh colors
        ],
      },

      deleteV2ProviderEncounterTypesByEncounterTypeId: {
        invalidatesTags: (result, error, { encounterTypeId }) => [
          { type: 'EncounterType', id: encounterTypeId },
          { type: 'EncounterTypesList', id: 'LIST' },
          { type: 'AppointmentsList', id: 'LIST' }, // Invalidate appointments using deleted type
        ],
      },
    },
  })
  .injectEndpoints({
    endpoints: builder => ({
      // Add any custom endpoints not covered by OpenAPI
      // For now, we'll use the generated endpoints as they cover our needs
    }),
    overrideExisting: false,
  });

// Export hooks with user-friendly names
export const {
  // Map generated hook names to feature-specific names
  useGetV2ProviderEncounterTypesQuery: useGetEncounterTypesQuery,
  usePostV2ProviderEncounterTypesMutation: useCreateEncounterTypeMutation,
  useGetV2ProviderEncounterTypesByEncounterTypeIdQuery: useGetEncounterTypeQuery,
  usePatchV2ProviderEncounterTypesByEncounterTypeIdMutation: useUpdateEncounterTypeMutation,
  useDeleteV2ProviderEncounterTypesByEncounterTypeIdMutation: useDeleteEncounterTypeMutation,
} = encounterTypesApi;
